import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { Loader2, MoreHorizontal, Plus, Search, UserPlus2, X } from "lucide-react";
import type { AdmissionEnquiry, StaffRosterItem } from "@eduviaos/shared";
import { useAddCollaborator, useRemoveCollaborator, useStaffRoster } from "@/hooks/useAdmissions";

/**
 * CollaboratorsCard — Phase D5.
 *
 * Renders the secondary owners on an enquiry detail page. Adds /
 * removes are idempotent on the BE so a fast double-click can't
 * corrupt the list. Excludes the current primary `assigned_to` from
 * the picker (no point being your own collaborator).
 *
 * Empty state explains the difference between "owner" and
 * "collaborator" so a counsellor doesn't accidentally turn a real
 * assignment into a no-op collaborator add.
 */

interface CollaboratorsCardProps {
  enquiry: AdmissionEnquiry;
}

function displayName(s: StaffRosterItem | undefined, fallback: string): string {
  if (!s) return fallback;
  return s.full_name?.trim() || s.email;
}

function initialsOf(s: StaffRosterItem | undefined, fallback: string): string {
  const src = s?.full_name?.trim() || s?.email.split("@")[0] || fallback;
  return (
    src
      .split(/[.\s_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

export function CollaboratorsCard({ enquiry }: CollaboratorsCardProps) {
  const { ct } = useTerms();
  const { data: roster } = useStaffRoster();
  const addMut = useAddCollaborator(enquiry.id);
  const removeMut = useRemoveCollaborator(enquiry.id);

  const staffById = React.useMemo(() => {
    const m = new Map<string, StaffRosterItem>();
    for (const s of roster?.items ?? []) m.set(s.user_id, s);
    return m;
  }, [roster]);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  // Pickable staff = active roster minus the primary owner minus
  // anyone already collaborating.
  const pickable = React.useMemo(() => {
    const exclude = new Set<string>(enquiry.collaborators);
    if (enquiry.assigned_to) exclude.add(enquiry.assigned_to);
    let list = (roster?.items ?? []).filter((s) => !exclude.has(s.user_id));
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const name = (s.full_name ?? "").toLowerCase();
        return name.includes(q) || s.email.toLowerCase().includes(q);
      });
    }
    return list;
  }, [roster, enquiry.collaborators, enquiry.assigned_to, query]);

  const onAdd = async (user_id: string) => {
    try {
      await addMut.mutateAsync({ user_id });
      setPickerOpen(false);
      setQuery("");
      const name = displayName(staffById.get(user_id), user_id);
      toast.success(`Added ${name} as a collaborator.`);
    } catch (e) {
      toast.error((e as { message?: string } | null)?.message ?? "Couldn't add collaborator.");
    }
  };

  const onRemove = async (user_id: string) => {
    try {
      await removeMut.mutateAsync({ user_id });
      const name = displayName(staffById.get(user_id), user_id);
      toast.success(`Removed ${name} from collaborators.`);
    } catch (e) {
      toast.error((e as { message?: string } | null)?.message ?? "Couldn't remove collaborator.");
    }
  };

  return (
    <Card>
      <CardHeader className="border-border/60 flex flex-row items-center justify-between gap-2 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserPlus2 className="text-muted-foreground size-4" aria-hidden="true" />
          Collaborators
        </CardTitle>
        <DropdownMenu open={pickerOpen} onOpenChange={setPickerOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid="add-collaborator-trigger"
            >
              <Plus className="size-3.5" aria-hidden="true" />
              Add
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[280px] p-0">
            <div className="border-border/60 border-b p-2">
              <div className="relative">
                <Search
                  className="text-muted-foreground absolute left-2 top-1/2 size-3.5 -translate-y-1/2"
                  aria-hidden="true"
                />
                <Input
                  type="search"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={ct("Search staff…")}
                  className="h-8 pl-7 text-xs"
                  aria-label={ct("Search staff")}
                />
              </div>
            </div>
            <div className="max-h-[240px] overflow-y-auto py-1">
              {pickable.length === 0 ? (
                <div className="text-muted-foreground px-3 py-6 text-center text-xs">
                  {(roster?.items ?? []).length === 0
                    ? ct("No staff invited yet.")
                    : "Everyone available is already on this enquiry."}
                </div>
              ) : (
                pickable.map((s) => (
                  <DropdownMenuItem
                    key={s.user_id}
                    onSelect={(e) => {
                      e.preventDefault();
                      void onAdd(s.user_id);
                    }}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs"
                    data-testid={`collaborator-pick-${s.user_id}`}
                  >
                    <span
                      aria-hidden="true"
                      className="bg-muted text-muted-foreground inline-flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                    >
                      {initialsOf(s, "?")}
                    </span>
                    <span className="min-w-0">
                      <span className="text-foreground block truncate">{displayName(s, "?")}</span>
                      {s.full_name && (
                        <span className="text-muted-foreground block truncate text-[10px]">
                          {s.email}
                        </span>
                      )}
                    </span>
                  </DropdownMenuItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent className="pt-3">
        {enquiry.collaborators.length === 0 ? (
          <p className="text-muted-foreground text-xs leading-relaxed">
            No collaborators yet. Collaborators get visibility on this enquiry without taking the
            primary owner role.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {enquiry.collaborators.map((uid) => {
              const s = staffById.get(uid);
              return (
                <li
                  key={uid}
                  className={cn(
                    "border-border/60 bg-card flex items-center gap-2 rounded-md border px-2 py-1.5",
                  )}
                  data-testid={`collaborator-${uid}`}
                >
                  <span
                    aria-hidden="true"
                    className="bg-muted text-muted-foreground inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                  >
                    {initialsOf(s, uid.slice(0, 2))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-foreground block truncate text-xs">
                      {displayName(s, uid)}
                    </span>
                    {s?.email && s.email !== displayName(s, uid) && (
                      <span className="text-muted-foreground block truncate text-[10px]">
                        {s.email}
                      </span>
                    )}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Actions for ${displayName(s, uid)}`}
                        className="size-7"
                      >
                        {removeMut.isPending && removeMut.variables?.user_id === uid ? (
                          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                        ) : (
                          <MoreHorizontal className="size-3.5" aria-hidden="true" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[140px]">
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => void onRemove(uid)}
                        data-testid={`collaborator-remove-${uid}`}
                      >
                        <X className="size-3.5" aria-hidden="true" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
